import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../api';
import RevenueChart from './RevenueChart';
import { formatInr, formatDate } from './checkout';
import './billing.css';

/* ==================================================================
   AdminBillingPanel
   ------------------------------------------------------------------
   The monetization section of the EXISTING admin dashboard — mounted
   into it, not a second admin app. It follows that page's conventions
   (a `.card` panel, `.section-header`, sub-tabs) so it reads as part
   of the same tool.

   Every endpoint it calls is behind auth + adminOnly on the server.
   Hiding this component would not protect anything, and it is not
   what protects it.
   ================================================================== */

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const TABS = [
  { id: 'revenue', label: 'Revenue' },
  { id: 'payments', label: 'Payments' },
  { id: 'entitlements', label: 'Entitlements' },
  { id: 'unlocks', label: 'Profile Unlocks' },
  { id: 'jobs', label: 'Job Posts' },
  { id: 'audit', label: 'Audit Trail' },
];

const PAYMENT_PILL = {
  paid: 'bl-pill--active',
  created: 'bl-pill--none',
  failed: 'bl-pill--danger',
  refunded: 'bl-pill--expired',
};

function Pill({ status, children }) {
  return <span className={`bl-pill ${PAYMENT_PILL[status] || 'bl-pill--none'}`}>{children || status}</span>;
}

function Empty({ children }) {
  return (
    <div className="bl-empty">
      <div className="bl-empty-title">Nothing to show</div>
      <p>{children}</p>
    </div>
  );
}

/* ── Refund dialog ──────────────────────────────────────────────────
   Records a refund that has ALREADY been issued in the Razorpay
   dashboard. It does not move money — the wording says so, because an
   admin who believed this button issued the refund would double-refund
   the customer. */
function RefundDialog({ payment, onClose, onDone }) {
  const [refundId, setRefundId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!payment) return null;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/admin/billing/payments/${payment.id}/refund`,
        { razorpay_refund_id: refundId.trim() || undefined, reason: reason.trim() || undefined },
        { headers: authHeaders() }
      );
      onDone();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record the refund.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="card"
        style={{ maxWidth: 460, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bl-refund-title"
      >
        <h3 id="bl-refund-title" style={{ fontSize: 17, marginBottom: 8 }}>Record a refund</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
          This records a refund you have <strong>already issued in the Razorpay dashboard</strong>.
          It does not move any money. Recording it here retires the package, recalculates the
          recruiter&rsquo;s validity window, and re-locks profiles that package was paying for.
          Credits they have already spent are not returned to them.
        </p>

        <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
          <div><strong>{payment.recruiter?.company_name || payment.recruiter?.name || 'Unknown recruiter'}</strong></div>
          <div style={{ color: 'var(--text-muted)' }}>
            {payment.package_name} · {formatInr(payment.amount_paise)} · {payment.razorpay_payment_id || payment.razorpay_order_id}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="bl-refund-id">Razorpay refund ID (optional)</label>
          <input
            id="bl-refund-id"
            value={refundId}
            onChange={(e) => setRefundId(e.target.value)}
            placeholder="rfnd_xxxxxxxxxxxx"
          />
        </div>
        <div className="form-group">
          <label htmlFor="bl-refund-reason">Reason (optional)</label>
          <input
            id="bl-refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Customer request"
          />
        </div>

        {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
            {saving ? 'Recording…' : 'Record refund'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBillingPanel() {
  const [tab, setTab] = useState('revenue');
  const [revenue, setRevenue] = useState(null);
  const [payments, setPayments] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [jobs, setJobs] = useState(null);
  const [audit, setAudit] = useState([]);
  const [reconcile, setReconcile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const get = useCallback(async (path) => {
    const res = await axios.get(`${API_URL}/api/admin/billing/${path}`, { headers: authHeaders() });
    return res.data;
  }, []);

  const loadRevenue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rev, rec] = await Promise.all([get('revenue?months=12'), get('reconcile')]);
      setRevenue(rev);
      setReconcile(rec);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load revenue.');
    } finally {
      setLoading(false);
    }
  }, [get]);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get(`payments?limit=100${statusFilter ? `&status=${statusFilter}` : ''}`);
      setPayments(data.payments || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load payments.');
    } finally {
      setLoading(false);
    }
  }, [get, statusFilter]);

  // One loader per tab, run on switch — the admin dashboard is already a heavy
  // page and loading six datasets it may never show would make it heavier.
  useEffect(() => {
    if (tab === 'revenue') loadRevenue();
    else if (tab === 'payments') loadPayments();
    else {
      const paths = {
        entitlements: ['entitlements', setEntitlements, 'entitlements'],
        unlocks: ['unlocks?limit=200', setUnlocks, 'unlocks'],
        jobs: ['jobs', setJobs, null],
        audit: ['audit?limit=150', setAudit, 'events'],
      };
      const entry = paths[tab];
      if (!entry) return;
      const [path, setter, key] = entry;
      setLoading(true);
      setError(null);
      get(path)
        .then((data) => setter(key ? (data[key] || []) : data))
        .catch((err) => setError(err.response?.data?.error || 'Could not load that view.'))
        .finally(() => setLoading(false));
    }
  }, [tab, get, loadRevenue, loadPayments]);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="section-header">
        <h2>Payments &amp; Monetization</h2>
        {loading && <div className="spinner" />}
      </div>

      <div className="bl-subtabs" role="tablist" aria-label="Billing sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`bl-subtab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bl-banner bl-banner--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" />
          </svg>
          <div className="bl-banner-body">{error}</div>
        </div>
      )}

      {/* ── Revenue ── */}
      {tab === 'revenue' && revenue && (
        <>
          <div className="bl-stats">
            <div className="bl-stat">
              <div className="bl-stat-label">Total revenue</div>
              <div className="bl-stat-value bl-stat-value--green">{formatInr(revenue.total_revenue_paise)}</div>
              <div className="bl-stat-hint">{revenue.successful_payments} successful payments</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Profile access</div>
              <div className="bl-stat-value">{formatInr(revenue.profile_revenue_paise)}</div>
              <div className="bl-stat-hint">{revenue.profile_payments} payments</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Job posts</div>
              <div className="bl-stat-value">{formatInr(revenue.job_revenue_paise)}</div>
              <div className="bl-stat-hint">{revenue.job_payments} payments</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Refunded</div>
              <div className="bl-stat-value bl-stat-value--amber">{formatInr(revenue.refunded_paise)}</div>
              <div className="bl-stat-hint">{revenue.refunded_payments} refunds</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Failed / pending</div>
              <div className="bl-stat-value bl-stat-value--red">
                {revenue.failed_payments} <span style={{ fontSize: 15, color: 'var(--text-muted)', fontWeight: 500 }}>/ {revenue.pending_payments}</span>
              </div>
              <div className="bl-stat-hint">Never granted credits</div>
            </div>
          </div>

          <RevenueChart series={revenue.series || []} />

          {/* Reconciliation. A clean report is the normal case, and saying so
              plainly is more useful than an empty panel that could equally
              mean "nothing checked". */}
          {reconcile && (
            <div className={`bl-banner ${reconcile.issues_found === 0 ? 'bl-banner--info' : 'bl-banner--warn'}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                {reconcile.issues_found === 0
                  ? <polyline points="20 6 9 17 4 12" />
                  : <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" /></>}
              </svg>
              <div className="bl-banner-body">
                <strong>
                  {reconcile.issues_found === 0
                    ? `Credit ledger balances — ${reconcile.wallets_checked} wallets checked`
                    : `${reconcile.issues_found} of ${reconcile.wallets_checked} wallets do not balance`}
                </strong>
                {reconcile.issues_found === 0
                  ? 'Every wallet’s purchased and consumed credits match the purchases and unlocks behind them.'
                  : 'These wallets disagree with the purchases and unlocks behind them and should be investigated:'}
                {reconcile.issues.map((issue, i) => (
                  <div key={i} style={{ marginTop: 8, fontSize: 12.5 }}>
                    <strong>{issue.recruiter?.company_name || issue.recruiter?.name || 'Unknown'} · {issue.product}</strong>
                    {issue.problems.map((p) => <div key={p}>— {p}</div>)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Payments ── */}
      {tab === 'payments' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {[
              { value: '', label: 'All' },
              { value: 'paid', label: 'Paid' },
              { value: 'created', label: 'Pending' },
              { value: 'failed', label: 'Failed' },
              { value: 'refunded', label: 'Refunded' },
            ].map((f) => (
              <button
                key={f.value}
                className={`btn btn-sm ${statusFilter === f.value ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {payments.length === 0 && !loading ? (
            <Empty>No payments match this filter.</Empty>
          ) : (
            <div className="bl-table-wrap">
              <table className="bl-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Recruiter</th>
                    <th>Package</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Razorpay</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(p.paid_at || p.created_at)}</td>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {p.recruiter?.company_name || '—'}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.recruiter?.email}</div>
                      </td>
                      <td>
                        {p.package_name}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {p.credits} {p.product === 'profile' ? 'profiles' : 'jobs'}
                        </div>
                      </td>
                      <td className="bl-num">{formatInr(p.amount_paise)}</td>
                      <td>
                        <Pill status={p.status} />
                        {/* Two independent facts an admin needs when a payment
                            looks odd: did the signature verify, and did the
                            webhook ever arrive. */}
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                          {p.signature_verified ? '✓ signature' : '· unverified'}
                          {p.webhook_received_at ? ' · ✓ webhook' : ''}
                        </div>
                      </td>
                      <td style={{ fontSize: 11.5, fontFamily: 'ui-monospace, monospace', color: 'var(--text-muted)' }}>
                        <div>{p.razorpay_order_id}</div>
                        {p.razorpay_payment_id && <div>{p.razorpay_payment_id}</div>}
                      </td>
                      <td>
                        {p.status === 'paid' && (
                          <button className="btn btn-secondary btn-sm" onClick={() => setRefundTarget(p)}>
                            Refund
                          </button>
                        )}
                        {p.status === 'refunded' && p.refund && (
                          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                            {formatInr(p.refund.amount_paise)}<br />{formatDate(p.refund.refunded_at)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Entitlements ── */}
      {tab === 'entitlements' && (
        entitlements.length === 0 && !loading ? (
          <Empty>No recruiter has bought credits yet.</Empty>
        ) : (
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Recruiter</th>
                  <th>Product</th>
                  <th>Purchased</th>
                  <th>Used</th>
                  <th>Remaining</th>
                  <th>Free used</th>
                  <th>Expires</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entitlements.map((e, i) => (
                  <tr key={`${e.recruiter?.id}-${e.product}-${i}`}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{e.recruiter?.company_name || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.recruiter?.email}</div>
                    </td>
                    <td>{e.product === 'profile' ? 'Profile access' : 'Job posts'}</td>
                    <td className="bl-num">{e.credits_total}</td>
                    <td className="bl-num">{e.credits_used}</td>
                    <td className="bl-num">
                      {e.credits_remaining}
                      {e.credits_stranded > 0 && (
                        <span style={{ color: '#B45309', fontSize: 11.5 }}> (+{e.credits_stranded} expired)</span>
                      )}
                    </td>
                    <td className="bl-num">{e.free_credits_used}</td>
                    <td>{formatDate(e.expires_at) || '—'}</td>
                    <td>
                      {e.active
                        ? <span className="bl-pill bl-pill--active">Active</span>
                        : <span className="bl-pill bl-pill--expired">Expired</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── Profile unlocks ── */}
      {tab === 'unlocks' && (
        unlocks.length === 0 && !loading ? (
          <Empty>No profiles have been unlocked yet.</Empty>
        ) : (
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Recruiter</th>
                  <th>Student</th>
                  <th>Unlocked</th>
                  <th>Paid with</th>
                  <th>Package</th>
                  <th>Access until</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {unlocks.map((u) => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.recruiter?.company_name || '—'}</td>
                    <td>
                      {u.student?.name || '—'}
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.student?.college}</div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(u.unlocked_at)}</td>
                    <td>
                      {u.credit_type === 'free' ? <span className="bl-pill bl-pill--free">Free trial</span>
                        : u.credit_type === 'legacy' ? <span className="bl-pill bl-pill--none">Legacy</span>
                          : <span className="bl-pill bl-pill--active">Paid credit</span>}
                    </td>
                    <td>{u.package || '—'}</td>
                    <td>{formatDate(u.access_expires_at) || (u.credit_type === 'free' ? 'No expiry' : '—')}</td>
                    <td>
                      {u.active
                        ? <span className="bl-pill bl-pill--active">Accessible</span>
                        : <span className="bl-pill bl-pill--expired">Ended</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── Job posts ── */}
      {tab === 'jobs' && jobs && (
        <>
          <div className="bl-stats">
            <div className="bl-stat">
              <div className="bl-stat-label">Total jobs</div>
              <div className="bl-stat-value">{jobs.summary.total}</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Live</div>
              <div className="bl-stat-value bl-stat-value--green">{jobs.summary.active}</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Drafts</div>
              <div className="bl-stat-value">{jobs.summary.drafts}</div>
              <div className="bl-stat-hint">Free — never charged</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Expired</div>
              <div className="bl-stat-value bl-stat-value--amber">{jobs.summary.expired}</div>
              <div className="bl-stat-hint">Ran their 30 days</div>
            </div>
            <div className="bl-stat">
              <div className="bl-stat-label">Paid posts</div>
              <div className="bl-stat-value">{jobs.summary.paid}</div>
              <div className="bl-stat-hint">
                {jobs.summary.free_legacy > 0
                  ? `${jobs.summary.free_legacy} live from before paid posting`
                  : 'All live jobs used a credit'}
              </div>
            </div>
          </div>

          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Recruiter</th>
                  <th>Status</th>
                  <th>Credit</th>
                  <th>Package</th>
                  <th>Published</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {jobs.jobs.map((j) => (
                  <tr key={j.id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{j.title}</td>
                    <td>{j.recruiter?.company_name || '—'}</td>
                    <td>
                      {j.status === 'published' ? <span className="bl-pill bl-pill--active">Live</span>
                        : j.expired_at ? <span className="bl-pill bl-pill--expired">Expired</span>
                          : j.status === 'closed' ? <span className="bl-pill bl-pill--none">Closed</span>
                            : <span className="bl-pill bl-pill--none">Draft</span>}
                    </td>
                    <td>{j.credit_consumed ? '1 used' : '—'}</td>
                    <td>{j.package || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(j.published_at) || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(j.expires_at) || '—'}
                      {j.days_remaining !== null && j.days_remaining !== undefined && (
                        <div style={{ fontSize: 11.5, color: j.days_remaining <= 5 ? '#B45309' : 'var(--text-muted)' }}>
                          {j.days_remaining} days left
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Audit ── */}
      {tab === 'audit' && (
        audit.length === 0 && !loading ? (
          <Empty>Financial and entitlement events will be recorded here.</Empty>
        ) : (
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>By</th>
                  <th>Recruiter</th>
                  <th>Amount</th>
                  <th>Credits</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(e.created_at).toLocaleString('en-IN', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{e.event}</td>
                    <td>{e.actor_type}</td>
                    <td>{e.recruiter?.company_name || '—'}</td>
                    <td className="bl-num">{e.amount_paise != null ? formatInr(e.amount_paise) : '—'}</td>
                    <td className="bl-num" style={{ color: e.credits_delta < 0 ? 'var(--danger)' : 'var(--accent-dark)' }}>
                      {e.credits_delta != null ? (e.credits_delta > 0 ? `+${e.credits_delta}` : e.credits_delta) : '—'}
                    </td>
                    <td style={{ fontSize: 11.5, color: 'var(--text-muted)', maxWidth: 260 }}>
                      {Object.entries(e.meta || {})
                        .filter(([, v]) => v !== null && v !== undefined && v !== '')
                        .slice(0, 3)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <RefundDialog
        payment={refundTarget}
        onClose={() => setRefundTarget(null)}
        onDone={loadPayments}
      />
    </div>
  );
}
